import { Link } from "react-router-dom";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { FloatingWhatsApp } from "@/components/FloatingWhatsApp";
import { 
  Shield, 
  FileCheck, 
  AlertTriangle, 
  CheckCircle, 
  ArrowRight,
  Globe,
  BookOpen,
  Scale,
  Building2,
  Users
} from "lucide-react";
import { useState, useEffect } from "react";

const HERO_BG = "https://images.unsplash.com/photo-1589829545856-d10d557cf95f?auto=format&fit=crop&w=2000&q=80";

const tableOfContents = [
  { id: "introduction", title: "Introduction" },
  { id: "risks", title: "Common Risks" },
  { id: "due-diligence", title: "Due Diligence Process" },
  { id: "title-verification", title: "Title Verification" },
  { id: "why-lawyer", title: "Why You Need a Lawyer" },
  { id: "diaspora-tips", title: "Tips for Diaspora Investors" },
  { id: "next-steps", title: "Next Steps" },
];

const commonRisks = [
  {
    title: "Fraudulent Sellers",
    description: "Individuals posing as property owners or agents selling land they don't legally own."
  },
  {
    title: "Disputed Titles",
    description: "Properties with ownership conflicts, family disputes, or government acquisition issues."
  },
  {
    title: "Forged Documents",
    description: "Fake Certificates of Occupancy, Deeds of Assignment, or Governor's Consent documents."
  },
  {
    title: "Encumbered Properties",
    description: "Land with existing mortgages, liens, or legal restrictions that affect ownership transfer."
  },
  {
    title: "Government Acquisitions",
    description: "Properties affected by government compulsory acquisition or designated for public use."
  },
  {
    title: "Community Land Issues",
    description: "Land still under customary ownership with unresolved family or community claims."
  }
];

const dueDiligenceSteps = [
  {
    step: "01",
    title: "Document Collection",
    description: "Gather all property documents from the seller including title documents, survey plans, and proof of ownership."
  },
  {
    step: "02",
    title: "Registry Search",
    description: "Conduct searches at relevant land registries to verify document authenticity and ownership records."
  },
  {
    step: "03",
    title: "Physical Verification",
    description: "Inspect the property physically, verify boundaries, and confirm the land matches documented descriptions."
  },
  {
    step: "04",
    title: "Encumbrance Check",
    description: "Verify there are no existing mortgages, court orders, or legal restrictions on the property."
  },
  {
    step: "05",
    title: "Legal Opinion",
    description: "Receive a comprehensive legal opinion on the property's status and any identified risks."
  }
];

const titleTypes = [
  {
    title: "Certificate of Occupancy (C of O)",
    description: "The highest form of land title in Nigeria, issued by the State Governor. It grants the holder rights to occupy and use the land for a specified period (usually 99 years).",
    strength: "Strongest"
  },
  {
    title: "Governor's Consent",
    description: "Required when transferring land with an existing C of O. Without it, the transaction is incomplete and the buyer's interest is not legally protected.",
    strength: "Essential"
  },
  {
    title: "Deed of Assignment",
    description: "A legal document that transfers ownership from seller to buyer. Must be registered and stamped to be legally valid.",
    strength: "Important"
  },
  {
    title: "Survey Plan",
    description: "An official document showing the property's exact location, boundaries, and dimensions. Essential for identifying the land.",
    strength: "Required"
  }
];

export default function PropertyGuidePage() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <main className="min-h-screen bg-[#FAFAF9]" data-testid="property-guide-page">
      <Navigation scrolled={scrolled} />
      
      {/* Hero Section */}
      <section className="relative min-h-[60vh] md:min-h-[70vh] flex items-center">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_BG})` }}
        />
        <div className="absolute inset-0 bg-[#0A0A0B]/85" />
        
        <div className="relative z-10 max-w-4xl mx-auto px-5 md:px-8 lg:px-12 py-32 md:py-40 text-center">
          <span className="inline-flex items-center gap-2 text-[#D4AF37] text-xs md:text-sm tracking-[0.2em] uppercase font-semibold mb-6">
            <BookOpen className="w-4 h-4" />
            Legal Guide
          </span>
          
          <h1 className="font-['Playfair_Display'] text-3xl md:text-4xl lg:text-5xl xl:text-6xl text-white leading-tight mb-6">
            Buying Property in Nigeria Safely:
            <span className="block text-[#D4AF37] italic mt-2">A Legal Guide for Diaspora Investors</span>
          </h1>
          
          <p className="text-white/70 text-base md:text-lg lg:text-xl max-w-2xl mx-auto mb-8">
            Everything you need to know about property due diligence, title verification, 
            and protecting your investment when buying land in Nigeria from abroad.
          </p>
          
          <div className="flex flex-wrap justify-center gap-3 text-xs md:text-sm text-white/50">
            <span className="flex items-center gap-1">
              <Globe className="w-3 h-3" /> For Diaspora Investors
            </span>
            <span className="hidden md:inline">•</span>
            <span>Updated December 2025</span>
            <span className="hidden md:inline">•</span>
            <span>15 min read</span>
          </div>
        </div>
      </section>

      {/* Table of Contents */}
      <section className="bg-[#0A0A0B] border-y border-[#D4AF37]/20 py-8 md:py-10">
        <div className="max-w-4xl mx-auto px-5 md:px-8">
          <h2 className="text-[#D4AF37] text-xs tracking-[0.2em] uppercase font-semibold mb-4">
            In This Guide
          </h2>
          <div className="flex flex-wrap gap-3 md:gap-4">
            {tableOfContents.map((item) => (
              <a
                key={item.id}
                href={`#${item.id}`}
                className="text-white/60 text-sm hover:text-[#D4AF37] transition-colors"
              >
                {item.title}
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Main Content */}
      <article className="max-w-4xl mx-auto px-5 md:px-8 lg:px-12 py-16 md:py-24">
        
        {/* Introduction */}
        <section id="introduction" className="mb-16 md:mb-20">
          <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl lg:text-4xl text-[#1A1A1A] mb-6">
            Introduction
          </h2>
          <div className="prose prose-lg max-w-none text-[#1A1A1A]/70 space-y-5">
            <p>
              Nigeria's real estate market offers significant investment opportunities, with property 
              values in major cities like Lagos, Abuja, and Port Harcourt showing consistent growth. 
              For Nigerians in the diaspora, investing in property back home is both a financial 
              decision and an emotional connection to their roots.
            </p>
            <p>
              However, the Nigerian property market also presents unique challenges. Without proper 
              legal guidance, investors risk losing their hard-earned money to fraud, disputed titles, 
              or invalid transactions. This guide provides essential information for anyone considering 
              buying property in Nigeria from abroad.
            </p>
            <p className="font-semibold text-[#1A1A1A]">
              The key to a successful property investment in Nigeria is thorough due diligence 
              conducted by a qualified property lawyer before any payment is made.
            </p>
          </div>
        </section>

        {/* Common Risks */}
        <section id="risks" className="mb-16 md:mb-20">
          <span className="text-[#D4AF37] text-xs tracking-[0.2em] uppercase font-semibold">
            Understanding the Challenges
          </span>
          <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl lg:text-4xl text-[#1A1A1A] mt-3 mb-8">
            Common Risks When Buying Property in Nigeria
          </h2>
          
          <div className="grid md:grid-cols-2 gap-4 md:gap-6">
            {commonRisks.map((risk, index) => (
              <div 
                key={index}
                className="p-5 md:p-6 border border-[#E5E5E5] hover:border-[#D4AF37]/50 transition-colors"
              >
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-[#1A1A1A] mb-2">{risk.title}</h3>
                    <p className="text-sm text-[#1A1A1A]/60 leading-relaxed">{risk.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          <div className="mt-8 p-6 bg-[#0A0A0B] text-white">
            <p className="text-sm md:text-base">
              <strong className="text-[#D4AF37]">Important:</strong> These risks can be significantly 
              reduced or eliminated through proper legal due diligence conducted before payment. 
              Never pay for property without first engaging a qualified property lawyer.
            </p>
          </div>
        </section>

        {/* Due Diligence Process */}
        <section id="due-diligence" className="mb-16 md:mb-20">
          <span className="text-[#D4AF37] text-xs tracking-[0.2em] uppercase font-semibold">
            The Essential Process
          </span>
          <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl lg:text-4xl text-[#1A1A1A] mt-3 mb-4">
            What is Property Due Diligence?
          </h2>
          
          <p className="text-[#1A1A1A]/70 text-base md:text-lg mb-10">
            Property due diligence is a comprehensive legal investigation conducted before purchasing 
            property. It involves reviewing ownership history, verifying title documents, confirming 
            land registry records, and identifying any legal issues affecting the property.
          </p>
          
          <div className="space-y-6">
            {dueDiligenceSteps.map((item, index) => (
              <div 
                key={index}
                className="flex gap-4 md:gap-6 p-5 md:p-6 border-l-2 border-[#D4AF37] bg-[#FAFAF9]"
              >
                <span className="font-['Playfair_Display'] text-3xl md:text-4xl text-[#D4AF37]/30 font-bold">
                  {item.step}
                </span>
                <div>
                  <h3 className="font-semibold text-[#1A1A1A] text-lg mb-2">{item.title}</h3>
                  <p className="text-[#1A1A1A]/60 text-sm md:text-base">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Title Verification */}
        <section id="title-verification" className="mb-16 md:mb-20">
          <span className="text-[#D4AF37] text-xs tracking-[0.2em] uppercase font-semibold">
            Understanding Land Titles
          </span>
          <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl lg:text-4xl text-[#1A1A1A] mt-3 mb-4">
            How to Verify Land Title in Nigeria
          </h2>
          
          <p className="text-[#1A1A1A]/70 text-base md:text-lg mb-10">
            Title verification involves searching government land registries and confirming the 
            authenticity of property documents. This process ensures the seller has legal authority 
            to transfer ownership and that no issues affect the property.
          </p>
          
          <h3 className="font-['Playfair_Display'] text-xl md:text-2xl text-[#1A1A1A] mb-6">
            Types of Land Titles in Nigeria
          </h3>
          
          <div className="space-y-4">
            {titleTypes.map((title, index) => (
              <div 
                key={index}
                className="p-5 md:p-6 border border-[#E5E5E5] hover:border-[#D4AF37]/50 transition-colors"
              >
                <div className="flex items-start justify-between gap-4 mb-3">
                  <h4 className="font-semibold text-[#1A1A1A]">{title.title}</h4>
                  <span className="text-xs bg-[#D4AF37]/10 text-[#C5A028] px-2 py-1 font-semibold">
                    {title.strength}
                  </span>
                </div>
                <p className="text-sm text-[#1A1A1A]/60 leading-relaxed">{title.description}</p>
              </div>
            ))}
          </div>
          
          <div className="mt-8 p-6 border-2 border-[#D4AF37]/30 bg-[#D4AF37]/5">
            <h4 className="font-semibold text-[#1A1A1A] mb-3 flex items-center gap-2">
              <Scale className="w-5 h-5 text-[#D4AF37]" />
              Verification Process
            </h4>
            <ul className="space-y-2 text-sm text-[#1A1A1A]/70">
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                Search at the State Land Registry to confirm title authenticity
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                Verify survey plan at the Office of the Surveyor General
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                Check for government acquisition at relevant ministry
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                Confirm no court cases or disputes affecting the property
              </li>
            </ul>
          </div>
        </section>

        {/* Why You Need a Lawyer */}
        <section id="why-lawyer" className="mb-16 md:mb-20">
          <span className="text-[#D4AF37] text-xs tracking-[0.2em] uppercase font-semibold">
            Professional Legal Support
          </span>
          <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl lg:text-4xl text-[#1A1A1A] mt-3 mb-4">
            Do I Need a Lawyer When Buying Property in Nigeria?
          </h2>
          
          <p className="text-[#1A1A1A]/70 text-base md:text-lg mb-8">
            <strong className="text-[#1A1A1A]">Yes.</strong> A property lawyer protects your interests 
            by conducting due diligence, reviewing contracts, ensuring proper documentation, and 
            guiding the transaction through legal completion.
          </p>
          
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="p-6 bg-[#0A0A0B] text-white">
              <h3 className="font-['Playfair_Display'] text-xl text-[#D4AF37] mb-4">
                What a Property Lawyer Does
              </h3>
              <ul className="space-y-3 text-sm text-white/70">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                  Conducts comprehensive title searches and verification
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                  Reviews and negotiates purchase agreements
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                  Prepares legal documentation for the transaction
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                  Handles registration and title perfection
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                  Provides legal opinion on investment risks
                </li>
              </ul>
            </div>
            
            <div className="p-6 border border-[#E5E5E5]">
              <h3 className="font-['Playfair_Display'] text-xl text-[#1A1A1A] mb-4">
                Risks of Not Using a Lawyer
              </h3>
              <ul className="space-y-3 text-sm text-[#1A1A1A]/60">
                <li className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                  Paying for property with defective or forged titles
                </li>
                <li className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                  Signing contracts with unfavorable terms
                </li>
                <li className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                  Incomplete transactions that leave ownership unclear
                </li>
                <li className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                  Loss of investment to fraudulent schemes
                </li>
                <li className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                  Expensive legal battles over disputed property
                </li>
              </ul>
            </div>
          </div>
        </section>

        {/* Diaspora Tips */}
        <section id="diaspora-tips" className="mb-16 md:mb-20">
          <span className="text-[#D4AF37] text-xs tracking-[0.2em] uppercase font-semibold">
            For Nigerians Abroad
          </span>
          <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl lg:text-4xl text-[#1A1A1A] mt-3 mb-4">
            Can Nigerians in the Diaspora Safely Invest in Nigerian Real Estate?
          </h2>
          
          <p className="text-[#1A1A1A]/70 text-base md:text-lg mb-8">
            <strong className="text-[#1A1A1A]">Yes.</strong> With proper legal advisory and due 
            diligence, diaspora investors can safely acquire property in Nigeria. Professional legal 
            oversight helps ensure the transaction is transparent, documented, and protected from fraud.
          </p>
          
          <div className="bg-[#0A0A0B] p-6 md:p-8 mb-8">
            <h3 className="font-['Playfair_Display'] text-xl md:text-2xl text-white mb-6">
              Essential Tips for Diaspora Property Investors
            </h3>
            <div className="grid md:grid-cols-2 gap-4">
              {[
                "Never pay without proper due diligence",
                "Engage a lawyer before visiting properties",
                "Verify all documents through official channels",
                "Avoid deals that seem too good to be true",
                "Get everything in writing and properly documented",
                "Use secure payment methods with paper trails",
                "Visit the property in person when possible",
                "Don't rely solely on family or friends for transactions"
              ].map((tip, index) => (
                <div key={index} className="flex items-start gap-2 text-white/70 text-sm">
                  <CheckCircle className="w-4 h-4 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                  {tip}
                </div>
              ))}
            </div>
          </div>
          
          <div className="flex flex-wrap gap-3">
            {["United Kingdom", "United States", "Canada", "UAE", "Germany", "South Africa"].map((country) => (
              <span 
                key={country}
                className="inline-flex items-center gap-2 border border-[#D4AF37]/30 px-4 py-2 text-sm text-[#1A1A1A]/70"
              >
                <Globe className="w-3 h-3 text-[#D4AF37]" />
                {country}
              </span>
            ))}
          </div>
        </section>

        {/* Next Steps CTA */}
        <section id="next-steps" className="bg-[#0A0A0B] p-8 md:p-12 text-center">
          <span className="text-[#D4AF37] text-xs tracking-[0.2em] uppercase font-semibold">
            Take Action
          </span>
          <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl lg:text-4xl text-white mt-3 mb-4">
            Ready to Invest Safely?
          </h2>
          <p className="text-white/60 text-base md:text-lg max-w-2xl mx-auto mb-8">
            Don't risk your investment. Schedule a consultation with our property law team to discuss 
            your investment plans and learn how we can protect your interests.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/packages"
              className="inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-[#0A0A0B] px-8 py-4 font-semibold text-sm tracking-wide uppercase hover:bg-[#C5A028] transition-colors"
              data-testid="guide-packages-cta"
            >
              View Consultation Packages
              <ArrowRight className="w-4 h-4" />
            </Link>
            <a
              href="https://wa.me/2348032143826?text=Hello%204ACES%20Attorneys%2C%20I%20saw%20your%20website%20and%20would%20like%20to%20discuss%20a%20property%20transaction%20in%20Nigeria."
              className="inline-flex items-center justify-center gap-2 border border-[#D4AF37] text-[#D4AF37] px-8 py-4 font-semibold text-sm tracking-wide uppercase hover:bg-[#D4AF37] hover:text-[#0A0A0B] transition-colors"
              data-testid="guide-whatsapp-cta"
            >
              WhatsApp Enquiry
            </a>
          </div>
        </section>
      </article>

      {/* Related Services */}
      <section className="bg-[#FAFAF9] border-t border-[#E5E5E5] py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-5 md:px-8">
          <h2 className="font-['Playfair_Display'] text-xl md:text-2xl text-[#1A1A1A] mb-8 text-center">
            Our Property Legal Services
          </h2>
          <div className="grid md:grid-cols-3 gap-4">
            {[
              { icon: Shield, title: "Due Diligence", desc: "Comprehensive property investigation" },
              { icon: FileCheck, title: "Title Verification", desc: "Registry searches and validation" },
              { icon: Building2, title: "Acquisition Advisory", desc: "End-to-end transaction support" }
            ].map((service, index) => (
              <Link
                key={index}
                to="/#services"
                className="p-6 border border-[#E5E5E5] hover:border-[#D4AF37] text-center group transition-colors"
              >
                <service.icon className="w-8 h-8 text-[#D4AF37] mx-auto mb-3" />
                <h3 className="font-semibold text-[#1A1A1A] mb-1">{service.title}</h3>
                <p className="text-sm text-[#1A1A1A]/50">{service.desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
      <FloatingWhatsApp />
    </main>
  );
}
